/**
 * Client for the three unauthenticated call-test endpoints.
 *
 * Deliberately sends no token, no cookies and no credentials — the endpoints are public by
 * design, and `credentials: 'omit'` keeps a stray cookie from turning a CORS request into a
 * preflight failure. `Accept: application/json` is required: without it the API answers errors
 * with an HTML page rather than the JSON envelope.
 */

import { CALLTEST_BASE_URL, TIMEOUTS } from '@/config/targets'
import { classifyFailure, parseErrorEnvelope, type ApiFailure } from './api-failure'
import type { CallTestResult, CallTestSession } from './types'

/** Carries the classified failure so the UI can pick a sentence without re-deriving it. */
export class CallTestApiError extends Error {
	readonly failure: ApiFailure

	constructor(failure: ApiFailure) {
		super(`call test api: ${failure.kind}`)
		this.name = 'CallTestApiError'
		this.failure = failure
	}
}

/** 32 lowercase hex, per the API's own validation — checked here to save a guaranteed 422. */
const SESSION_ID = /^[0-9a-f]{32}$/

export async function createSession(): Promise<CallTestSession> {
	return request<CallTestSession>('/pbx/calltest/session', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: '{}'
	})
}

/**
 * Hands the SIP account back and returns the platform's own measurement. Idempotent — the phone
 * deletion tolerates an account that is already gone — so retrying after a network error is safe.
 */
export async function finishSession(sessionId: string): Promise<CallTestResult> {
	assertSessionId(sessionId)
	return request<CallTestResult>(`/pbx/calltest/session/${sessionId}/finish`, { method: 'POST' })
}

/** One read, no waiting. Only worth calling after a PENDING. */
export async function readResult(sessionId: string): Promise<CallTestResult> {
	assertSessionId(sessionId)
	return request<CallTestResult>(`/pbx/calltest/session/${sessionId}/result`, { method: 'GET' })
}

/**
 * Best-effort hand-back for a closing tab. `keepalive` lets the request outlive the document;
 * the response is never seen, and a miss is collected by the server's reaper within five minutes.
 */
export function releaseSessionOnExit(sessionId: string): void {
	if (!SESSION_ID.test(sessionId)) return

	try {
		void fetch(`${CALLTEST_BASE_URL}/pbx/calltest/session/${sessionId}/finish`, {
			method: 'POST',
			headers: { Accept: 'application/json' },
			credentials: 'omit',
			keepalive: true
		})
	} catch {
		// A closing document is not the place to surface anything.
	}
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), TIMEOUTS.callTestApi)

	let response: Response
	try {
		response = await fetch(`${CALLTEST_BASE_URL}${path}`, {
			...init,
			headers: { Accept: 'application/json', ...init.headers },
			cache: 'no-store',
			credentials: 'omit',
			signal: controller.signal
		})
	} catch {
		// Status 0 stands for "the request never left the browser".
		throw new CallTestApiError(classifyFailure(0, null))
	} finally {
		clearTimeout(timer)
	}

	// Read the body once, as text, so a non-JSON error page does not throw before it is classified.
	const raw = await response.text().catch(() => '')
	const body = parseJson(raw)

	if (!response.ok) {
		throw new CallTestApiError(classifyFailure(
			response.status,
			parseErrorEnvelope(body),
			response.headers.get('Retry-After') ?? undefined
		))
	}

	if (body === null) throw new CallTestApiError(classifyFailure(response.status, null))
	return body as T
}

function parseJson(raw: string): unknown {
	try {
		return JSON.parse(raw)
	} catch {
		return null
	}
}

function assertSessionId(sessionId: string): void {
	if (!SESSION_ID.test(sessionId)) throw new CallTestApiError(classifyFailure(422, 'id'))
}
